import { getPromoOffer } from '@/actions/ecom/promoOffer';
import { useAppConfigContext } from '@/context/AppContext';
import useModalToggle from '@/hooks/useModalToggle';
import useOnMount from '@/hooks/useOnMount';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import Button from '@/shared/Button';
import Modal from '@/shared/crisp/Modal';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import Image from '@/shared/Image';
import Paragraph from '@/shared/Paragraph';
import dlv from 'dlv';
import React, { Suspense, useEffect } from 'react';
import Map from './Map';
import MetadataTag from '@/shared/crisp/MetadataTag';
import useToast from '@/hooks/useToast';
import copyToClipboard from '@/utils/copyToClipboard';
import { useRouter } from 'next/router';
import useGTM from '@/hooks/useGTM';
import LocalStorage from '@/utils/localStorage';

const EVENTS = {
  promo_bottomsheet_opened: 'promo_bottomsheet_opened',
  promo_bottomsheet_cta_clicked: 'promo_bottomsheet_cta_clicked',
  promo_bottomsheet_closed: 'promo_bottomsheet_closed',
};

const PromoOffer = () => {
  const {
    open: isPromoOfferModalOpen,
    openModal: openPromoOfferModal,
    closeModal: closePromoOfferModal,
  } = useModalToggle();
  const { state: appConfig, dispatch: appConfigDispatch } = useAppConfigContext();
  const promoOfferReq = useTriggerRequest({ fetch: getPromoOffer });
  const { triggerSuccess } = useToast();
  const { fireGTMEvent } = useGTM();

  const router = useRouter();

  useOnMount(async () => {
    const promoOffers = await promoOfferReq.promisifyTrigger();
    appConfigDispatch.updateAppConfig({
      ...appConfig.config,
      promoOffers,
    });

    const { id, screens, delay = 0, flag } = dlv(promoOffers, 'promo1', null);

    const key = `efl_promo_shown_${id}`;
    if (flag && LocalStorage.get(key) !== 'true' && id && screens.includes(router.pathname)) {
      setTimeout(() => {
        openPromoOfferModal();
        LocalStorage.set(key, 'true');
      }, delay);
    }
  });

  const promo = dlv(appConfig, 'config.promoOffers.promo1', null);

  useEffect(() => {
    const key = `efl_promo_shown_${promo?.id}`;
    if (
      promo?.screens?.includes(router.pathname) &&
      promo?.flag &&
      !isPromoOfferModalOpen &&
      !appConfig.promoOffer.open &&
      LocalStorage.get(key) !== 'true'
    ) {
      setTimeout(() => {
        appConfigDispatch.togglePromoOffer();
        LocalStorage.set(key, 'true');
      }, promo?.delay);
    }
  }, [router.pathname]);

  useEffect(() => {
    if (appConfig.promoOffer.open) {
      if (isPromoOfferModalOpen || !promo.flag) {
        appConfigDispatch.togglePromoOffer();
        return;
      }
      openPromoOfferModal();
      fireGTMEvent(
        EVENTS.promo_bottomsheet_opened,
        { promo_code: promo.value, promo_id: promo.id, screen: router.pathname },
        {
          includeSelectedDevice: false,
          includeSourceCTA: false,
          webengage: {
            fire: true,
            isDirect: true,
          },
        },
      );
    } else {
      closePromoOfferModal();
    }
  }, [appConfig.promoOffer.open]);

  if (!promo || !promo.flag) {
    return null;
  }
  const { id, title, description, value, close, cta, image, tags, flag } = promo;

  if (!flag) return null;
  const [descBeforeValue, descAfterValue = ''] = description.split('{value}');

  const handleCtaClick = async () => {
    const success = await copyToClipboard(value);
    fireGTMEvent(
      EVENTS.promo_bottomsheet_cta_clicked,
      { promo_code: promo.value, promo_id: promo.id },
      {
        includeSelectedDevice: false,
        includeSourceCTA: false,
        webengage: {
          fire: true,
          isDirect: true,
        },
      },
    );
    if (success) {
      triggerSuccess({
        message: `Promo code ${value} copied to clipboard!`,
        position: 'top',
      });
    }
    if (cta && cta.href) {
      router.push({
        pathname: cta.href,
      });
    }
    closePromoOfferModal();
    if (appConfig.promoOffer.open) {
      appConfigDispatch.togglePromoOffer();
    }
  };

  const onModalClose = () => {
    fireGTMEvent(
      EVENTS.promo_bottomsheet_closed,
      { promo_code: promo.value, promo_id: promo.id },
      {
        includeSelectedDevice: false,
        includeSourceCTA: false,
        webengage: {
          fire: true,
          isDirect: true,
        },
      },
    );
    closePromoOfferModal();
    if (appConfig.promoOffer.open) {
      appConfigDispatch.togglePromoOffer();
    }
  };

  return (
    <Suspense>
      <Modal
        id={id}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        blockScroll={true}
        open={isPromoOfferModalOpen}
        onClose={onModalClose}
        showCloseButton={close}
        width="!bg-transparent md:rounded-b-cl"
        classNames="md:w-[496px] rounded-b-cl"
      >
        <Image src={image} alt="Promo Offer" className="w-full rounded-t-cl" />
        <Div className="bg-white pt-s1">
          <Map
            list={tags}
            className="mx-auto w-fit gap-s2 flex my-s4"
            render={(tag, i) => <MetadataTag color="Red" type="Metadata" label={tag} key={i} />}
          />
          <Div className="px-s5 pb-s5 md:px-s7 md:pb-s7 text-cti-primary text-center">
            <Heading className="mb-s3 text-h-2 md:text-h-1">{title}</Heading>
            <Paragraph className="text-body-lg">
              {descBeforeValue} <b>{value}</b> {descAfterValue}
            </Paragraph>
            {cta && cta.label && (
              <Button className="mt-5 w-full" onClick={handleCtaClick}>
                {cta.label}
              </Button>
            )}
          </Div>
        </Div>
      </Modal>
    </Suspense>
  );
};

export default React.memo(PromoOffer);
